import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Award, GraduationCap, ShieldCheck, Phone } from 'lucide-react';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import SiteFooter from '../components/SiteFooter';
import Breadcrumbs from '../components/Breadcrumbs';
import DirectAnswer from '../components/DirectAnswer';
import { Button } from '../components/ui/button';
import { aboutPageSEO, PUBLIC_SITE_URL } from '../data/seo';
import { PRACTICE, WHATSAPP_LINK } from '../data/practice';
import { getPersonClinicianLD, getProfilePageLD, getBreadcrumbLD, getSpeakableLD, getFAQPageLD } from '../data/structuredData';

const expertise = [
  { title: 'Invisalign & clear aligners', body: '150+ completed cases. Digital treatment planning with 3D scanning so you see the projected result before you start.', to: '/invisalign-acton' },
  { title: 'Dental implants', body: 'CBCT-guided placement, single teeth to full-arch All-on-4, including salvage of failing implants and bridgework.', to: '/implants-acton' },
  { title: 'Composite bonding & veneers', body: 'Minimally invasive, same-day smile corrections that preserve natural enamel.', to: '/composite-bonding-acton' },
  { title: 'Full-mouth restorative dentistry', body: 'Complex wear, bite collapse and multi-tooth rehabilitation planned as one cohesive case.', to: '/services/full-mouth-reconstruction' },
  { title: 'TMJ, bruxism & sleep-related dentistry', body: 'Jaw pain, grinding and sleep apnoea management with custom splints and bite therapy.', to: '/services/tmj-dentistry' },
  { title: 'Sedation for anxious patients', body: 'Calm, phobia-friendly appointments with sedation available for longer or surgical procedures.', to: '/services/sedation' },
];

const aboutFAQs = [
  { question: 'Who is Dr Kartik Patel?', answer: 'Dr Kartik Patel BDS is the founder and principal dentist of Restore.Dental at 64a Horn Lane, Acton, West London. He is registered with the General Dental Council (GDC 177993), qualified from King\'s College London and has over 15 years of clinical experience with a focus on Invisalign, dental implants and cosmetic and restorative dentistry.' },
  { question: 'Is Dr Kartik Patel an Invisalign provider?', answer: 'Yes. Dr Patel is an experienced Invisalign provider with more than 150 completed clear-aligner cases, using digital 3D scanning and smile simulation to plan treatment.' },
  { question: 'Where does Dr Kartik Patel practise?', answer: 'Dr Patel practises at Restore.Dental, 64a Horn Lane, Acton, London W3 6NP — four minutes\' walk from Acton Main Line (Elizabeth line), serving Acton, Ealing, Chiswick, Hammersmith and the wider West London area.' },
  { question: 'Does Dr Patel treat nervous patients?', answer: 'Yes. A large proportion of the practice\'s patients are anxious or have avoided the dentist for years. Appointments are unhurried, explanations are honest, and sedation is available for surgical or longer treatments.' },
];

export default function About() {
  const crumbs = [{ name: 'Home', to: '/' }, { name: 'About Dr Kartik Patel', to: '/about-dr-kartik-patel' }];
  return (
    <div className="min-h-screen bg-white">
      <SEO
        {...aboutPageSEO}
        structuredData={[
          getPersonClinicianLD(),
          getProfilePageLD(),
          getBreadcrumbLD(crumbs.map((c) => ({ name: c.name, url: `${PUBLIC_SITE_URL}${c.to}` }))),
          getSpeakableLD(['Dr Kartik Patel BDS is the principal dentist at Restore.Dental in Acton, West London']),
          getFAQPageLD(aboutFAQs),
        ]}
      />
      <PageHeader />

      <main className="pt-28 md:pt-32">
        <section className="container mx-auto px-4 max-w-5xl">
          <Breadcrumbs items={crumbs} />
          <div className="grid md:grid-cols-5 gap-10 items-start mt-6">
            <div className="md:col-span-2">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#01A9B4]/20 to-[#D4AF37]/20 rounded-2xl rotate-3" aria-hidden="true"></div>
                <img
                  src="/dr-kartik-patel.webp"
                  alt="Dr Kartik Patel BDS, principal dentist at Restore.Dental, Acton"
                  width="720"
                  height="675"
                  className="relative rounded-2xl shadow-2xl w-full h-auto object-cover"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
              <ul className="mt-8 space-y-3 text-sm text-chocolate-malt">
                <li className="flex gap-3"><GraduationCap className="w-5 h-5 text-[#01A9B4] flex-shrink-0" aria-hidden="true" /><span><strong>BDS</strong> — King&apos;s College London</span></li>
                <li className="flex gap-3"><ShieldCheck className="w-5 h-5 text-[#01A9B4] flex-shrink-0" aria-hidden="true" /><span><strong>GDC registration</strong> <a href={PRACTICE.links.gdcRegister} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">177993</a></span></li>
                <li className="flex gap-3"><Award className="w-5 h-5 text-[#01A9B4] flex-shrink-0" aria-hidden="true" /><span><strong>15+ years</strong> clinical experience · <strong>500+</strong> Invisalign cases</span></li>
              </ul>
            </div>

            <div className="md:col-span-3">
              <p className="text-sm font-semibold uppercase tracking-widest text-[#006A71] mb-3">Principal Dentist · Restore.Dental, Acton</p>
              <h1 className="text-4xl sm:text-5xl font-bold text-rabbit leading-tight mb-6">Dr Kartik Patel BDS</h1>
              <DirectAnswer
                answer="Dr Kartik Patel is the founder and principal dentist at Restore.Dental, 64a Horn Lane, Acton, West London. A King's College London graduate registered with the General Dental Council (177993), he has over 15 years' experience and focuses on Invisalign, dental implants, composite bonding and complex restorative dentistry — with a reputation for solving problems other practices couldn't fix."
                bullets={['Invisalign provider, 150+ cases', 'CBCT-guided implant surgery', 'Advanced training worldwide', 'Sedation for anxious patients']}
              />

              <div className="max-w-none mt-10 text-chocolate-malt">
                <h2 className="text-2xl md:text-3xl font-bold text-rabbit">A different kind of dentist</h2>
                <p>Dr Patel founded Restore.Dental on a simple principle: <strong>do it properly, or don&apos;t do it at all.</strong> That means no rushed appointments, no quick fixes that fail in two years, and no treatment recommended unless he would choose it for his own family.</p>
                <p>After qualifying from King&apos;s College London, he invested heavily in postgraduate training with leading clinicians around the world in implantology, clear-aligner orthodontics, adhesive and cosmetic dentistry, and occlusion (how teeth and jaws work together). That breadth is why patients with complex, multi-problem mouths — failing bridgework, severe wear, previous implant failures — are often referred to him.</p>

                <h2 className="text-2xl md:text-3xl font-bold text-rabbit">How Dr Patel works</h2>
                <ol>
                  <li><strong>Listen first.</strong> Your goals, your worries and your history before any examination.</li>
                  <li><strong>Diagnose thoroughly.</strong> Clinical photographs, digital X-rays and 3D scans of your teeth, bite, jaw and gums.</li>
                  <li><strong>Explain honestly.</strong> A written proposal with problems, options, costs and videos — sent to your WhatsApp so you can decide in your own time.</li>
                  <li><strong>Stay accountable.</strong> Once a patient, always a patient: a direct line to Dr Patel for the life of your treatment.</li>
                </ol>

                <blockquote className="border-l-4 border-[#01A9B4] pl-6 italic">
                  &ldquo;I approach every patient with one question: what would I want for my own family? That guides every decision, from the materials we choose to the time we take to get things right.&rdquo;
                  <footer className="not-italic text-sm mt-2">— Dr Kartik Patel</footer>
                </blockquote>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-sky-gray/10 py-16 mt-16">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-2xl md:text-3xl font-bold text-rabbit mb-8">Areas of clinical focus</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {expertise.map((e) => (
                <Link key={e.title} to={e.to} className="group bg-white rounded-xl p-6 border border-sky-gray/20 hover:border-burnt-coral transition-colors" data-testid={`expertise-${e.to.split('/').pop()}`}>
                  <CheckCircle2 className="w-6 h-6 text-[#01A9B4] mb-3" aria-hidden="true" />
                  <h3 className="font-bold text-rabbit mb-2 group-hover:text-burnt-coral">{e.title}</h3>
                  <p className="text-sm text-chocolate-malt">{e.body}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-rabbit mb-8">Frequently asked questions about Dr Patel</h2>
            <div className="space-y-6">
              {aboutFAQs.map((f) => (
                <div key={f.question} className="border-b border-sky-gray/20 pb-6">
                  <h3 className="text-lg font-bold text-rabbit mb-2">{f.question}</h3>
                  <p className="text-chocolate-malt leading-relaxed">{f.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-br from-sky-gray to-burnt-coral text-white py-16">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl font-bold mb-4">Book a consultation with Dr Patel</h2>
            <p className="text-lg opacity-90 mb-8">Same-week appointments at {PRACTICE.addressOneLine}. Free consultation when you proceed with treatment.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="secondary"><a href={PRACTICE.phoneTel} data-testid="about-call-btn"><Phone className="w-5 h-5 mr-2" />Call {PRACTICE.phoneDisplay}</a></Button>
              <Button asChild size="lg" className="bg-[#25D366] hover:bg-[#20BA5A] text-white"><a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="about-whatsapp-btn">WhatsApp Dr Patel&apos;s team</a></Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
